import {
  DistanceRefinement,
  FormRefinement,
  HandicapRefinement,
  RangeSliderFilter,
} from '~/components/BettingInsights/components/Filters';
import { PriceSwingRefinement } from '~/components/BettingInsights/components/Filters/premium-market-movers/PriceSwingRefinement.tsx';
import { MarketMoversCard } from '~/components/BettingInsights/components/MarketMoversCard';
import { Autocomplete } from '~/components/BettingInsights/components/Search/Autocomplete.tsx';
import { type BettingInsightsConfigFactoryOptions, type PremiumMarketMoversIndex } from '~/configuration/types';
import { type BettingInsightsUIConfig, createBettingInsightsUIConfig } from '~/configuration/ui/BettingInsightsUI';
import { premiumMarketMoversIndex } from '~/search-domain/indexes';
import { getMatchupTeamInfo } from '~/utils/getMatchupTeamInfo';
import { type RequestContext } from '~/vendor/cx-web/domain-types';
import { featureFlags } from '~/vendor/cx-web/feature-flags/feature-flag-provider';
import { getRepubMetaFromRepub } from '~/vendor/cx-web/utils';

export function createPremiumMarketMoversUIConfig(
  requestContext: RequestContext,
  _options: BettingInsightsConfigFactoryOptions = {},
  tags: string[],
) {
  const factoryRepub = requestContext.repubConfig.name;
  const { repubPath: factoryRepubPath } = getRepubMetaFromRepub(factoryRepub);

  const uiConfig: BettingInsightsUIConfig<PremiumMarketMoversIndex, any> = {
    card: {
      styles: {},
      table: {
        body: {
          render(_context, { bookmakers, content, resourcesUrl, table }) {
            return (
              <MarketMoversCard
                bookmakers={bookmakers}
                content={content}
                isLocked={table.isWalled}
                repub={factoryRepub}
                repubPath={factoryRepubPath}
                resourcesUrl={resourcesUrl || ''}
              />
            );
          },
        },
        footer: {
          render() {
            return null;
          },
        },
        getContent(_context, { result }) {
          return [result];
        },
        headings: {
          row: 1,
          content: [],
        },
        main: {
          render(_context) {
            return null;
          },
        },
        cells: [],
      },
      title: {
        format: ({ result }) => {
          const { categoryGroupName, subeventName, teamData } = result;
          const { matchupString } = getMatchupTeamInfo({ categoryGroupName, subeventName, teamData });

          return `{eventDisplayName} | ${matchupString} | {subeventStartTime}`;
        },
      },
      wrapper: (_context, { children }) => children,
    },
    initialUiState(_context) {
      return {
        hitsPerPage: 10,
      };
    },
    refinements: {
      clearAll: {
        excludedAttributes: [],
      },
      components: [
        {
          attribute: 'horseRacingSportsData.distance',
          getLabel(_context) {
            return 'Distance';
          },
          render(context) {
            return <DistanceRefinement attribute={context.attribute} isDisabled={context.isWalled} />;
          },
        },
        {
          attribute: 'horseRacingSportsData.handicap',
          getLabel(_context) {
            return 'Handicap';
          },
          render(context) {
            return (
              <HandicapRefinement
                attribute={context.attribute}
                isBooleanField={true}
                isDisabled={context.isWalled}
                label="Handicap"
              />
            );
          },
        },
        {
          attribute: 'horseRacingSportsData.runs',
          getLabel(_context) {
            return 'Number of Runs';
          },
          render(context) {
            return (
              <RangeSliderFilter
                attribute={context.attribute}
                data-analytics="minimumRuns"
                isDisabled={context.isWalled}
                label="Number of Runs"
                showReset
                withInputs={true}
              />
            );
          },
        },
        {
          attribute: 'horseRacingSportsData.form',
          getLabel(_context) {
            return 'Form';
          },
          render(context) {
            return <FormRefinement attribute={context.attribute} isDisabled={context.isWalled} label="Won or Placed" />;
          },
        },
        {
          attribute: 'marketMovement',
          getLabel(_context) {
            return 'Price Swing % Range';
          },
          render(context) {
            return (
              <PriceSwingRefinement
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label="Price Swing % Range"
              />
            );
          },
        },
      ],
    },
    results: {
      categories() {
        return {
          attribute: 'categoryGroupName',
          labels: {
            'HORSE RACING': {
              displayName: 'Market Movers',
              categoryGroupName: 'Market Movers',
            },
          },
          order: { start: ['HORSE RACING'] },
        };
      },
      labelledBy: '#premium-market-movers-title',
      propsName: 'bets',
    },
    search: {
      render(context, { searchAttributes }) {
        const textSearchEnabled =
          context.featureFlags.find(f => f.name === featureFlags.SUBS__PLUS__TEXT_SEARCH)?.enabled ?? false;

        if (!textSearchEnabled) {
          return null;
        }

        return (
          <Autocomplete
            disabled={context.isWalled}
            indexName={premiumMarketMoversIndex}
            placeholder="Search horse, owner, trainer or jockey"
            repub={context.repubConfig.name}
            searchAttributes={searchAttributes}
            searchOperator="OR"
          />
        );
      },
    },
    sorting: {
      canSort() {
        return true;
      },
    },
  };

  return { ui: createBettingInsightsUIConfig(requestContext, uiConfig, tags) };
}
